import { Controller } from '@hotwired/stimulus';
import { acceptedType } from '../js/file_types';

// Vérification d'un formulaire à l'envoi : règles posées par
// UiFormBuilder (data-check : required, minlength, maxlength, pattern,
// maxsize et accept des fichiers, optional), messages sous les champs et récapitulatif en haut
// (chaque erreur : lien vers son champ), focus sur le premier champ fautif
// (sa saisie si c'est une liste) ; un champ marqué est revérifié à la
// saisie. Le serveur reste seul juge : formulaire rendu avec ses erreurs
// (refus), focus sur le premier champ fautif, à défaut sur le
// récapitulatif (fenêtre : le contrôleur dialog s'en charge à l'ouverture).
export default class extends Controller {
    static targets = ['summary'];

    connect() {
        this.element.noValidate = true;
        if (this.hasSummaryTarget && !this.summaryTarget.hidden && !this.element.closest('dialog')) this.focusError();
    }

    focusError() {
        const invalid = [...this.element.querySelectorAll('[aria-invalid="true"]:not([role=combobox])')]
            .find((field) => !field.disabled && !field.parentElement.closest('[hidden]'));
        if (invalid) {
            this.focusField(invalid);
            return;
        }
        this.summaryTarget.tabIndex = -1;
        this.summaryTarget.focus();
    }

    // Champ, ou sa saisie si c'est une liste (combobox).
    focusField(field) {
        (document.getElementById(`${field.id}-combobox`) || field).focus();
    }

    // Lien du récapitulatif : focus sur le champ visé.
    jump(event) {
        const link = event.target.closest('a[href^="#"]');
        const field = link && document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (!field) return;
        event.preventDefault();
        this.focusField(field);
    }

    check(event) {
        const errors = this.fields.map((input) => [input, this.errorFor(input)]).filter(([, message]) => message);
        this.fields.forEach((input) => this.show(input, errors.find(([field]) => field === input)?.[1]));
        this.summarize(errors);
        if (errors.length === 0) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.focusField(errors[0][0]);
    }

    revalidate({ target }) {
        if (!target.dataset?.check || target.getAttribute('aria-invalid') !== 'true') return;
        this.show(target, this.errorFor(target));
    }

    // Champs vérifiés : ni désactivés ni dans un bloc masqué (choix du
    // propriétaire, champs selon le type ; le select d'une liste à recherche est
    // lui-même masqué).
    get fields() {
        return [...this.element.querySelectorAll('[data-check]')]
            .filter((field) => !field.disabled && !field.parentElement.closest('[hidden]'));
    }

    errorFor(input) {
        const rules = JSON.parse(input.dataset.check);
        if (input.type === 'file') {
            const file = input.files[0];
            if (!file) return rules.required || null;
            if (rules.accept && !acceptedType(file, rules.accept[0])) return rules.accept[1];
            return rules.maxsize && file.size > rules.maxsize[0] ? rules.maxsize[1] : null;
        }
        const value = input.value.trim();
        if (value === '') {
            if (rules.required) return rules.required;
            if (rules.pattern && !rules.optional && !new RegExp(rules.pattern[0]).test('')) return rules.pattern[1];
            return null;
        }
        if (rules.minlength && value.length < rules.minlength[0]) return rules.minlength[1];
        if (rules.maxlength && value.length > rules.maxlength[0]) return rules.maxlength[1];
        if (rules.pattern && !new RegExp(rules.pattern[0]).test(value)) return rules.pattern[1];
        return null;
    }

    show(input, message) {
        input.setAttribute('aria-invalid', message ? 'true' : 'false');
        document.getElementById(`${input.id}-combobox`)?.setAttribute('aria-invalid', message ? 'true' : 'false');
        const error = document.getElementById(`${input.id}_error`);
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
    }

    summarize(errors) {
        if (!this.hasSummaryTarget) return;
        const box = this.summaryTarget;
        box.replaceChildren();
        box.hidden = errors.length === 0;
        if (errors.length === 0) return;

        const title = document.createElement('p');
        title.className = 'font-semibold';
        title.textContent = 'Le formulaire contient des erreurs :';
        const list = document.createElement('ul');
        list.className = 'mt-1 list-disc pl-5';
        errors.forEach(([input, message]) => {
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = `#${input.id}`;
            link.className = 'underline';
            link.textContent = `${input.dataset.label} : ${message}`;
            item.append(link);
            list.append(item);
        });
        box.append(title, list);
    }
}
